import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import { Suspense, useMemo, useState } from "react"
import { AlertTriangle, Book, Edit, MoreHorizontal, Plus, Sliders, Trash } from "react-feather"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import * as z from "zod"
import { type PropertyModels, propertyApi } from "@/api/client/property"
import {
  type PropertyList200PropertiesItem,
  PropertyListIsEchoesLinked,
  PropertyListWithOwner,
  PropertyListWithRooms,
} from "@/api/generated/models"
import {
  EchoesSyncCell,
  echoesLinkedFilterOptions,
  echoesLinkedFilterValues,
  normalizeEchoesLinkedFilter,
} from "@/components/echoes/EchoesSyncCard"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { PermissionGate } from "@/features/auth/components/PermissionGate"
import { toast } from "@/hooks/use-toast"
import { openInNewTab } from "@/lib/navigation"

// 検索フォーム用のスキーマ
const searchFormSchema = z.object({
  propertyId: z.number().optional(),
  propertyName: z.string().optional(),
  propertyNameKana: z.string().optional(),
  postalCode: z.string().optional(),
  address: z.string().optional(),
  ownerId: z.string().optional(),
  ownerName: z.string().optional(),
  managementCompany: z.string().optional(),
  isEchoesLinked: z.enum(echoesLinkedFilterValues).optional(),
})

type SearchFormValues = z.infer<typeof searchFormSchema>

// nuqsのパーサー定義
const propertyPageParsers = {
  page: parseAsInteger.withDefault(1),
  propertyId: parseAsInteger,
  propertyName: parseAsString,
  propertyNameKana: parseAsString,
  postalCode: parseAsString,
  address: parseAsString,
  // オーナー詳細の「保有物件を見る」から渡される。検索フォームには出さない
  ownerId: parseAsString,
  ownerName: parseAsString,
  managementCompany: parseAsString,
  isEchoesLinked: parseAsString,
}

export function PropertyPage() {
  const itemsPerPage = 20
  const queryClient = useQueryClient()

  // URLクエリパラメーター管理
  const [queryParams, setQueryParams] = useQueryStates(propertyPageParsers)

  // フォームの値をクエリから復元
  const searchParams = useMemo(() => {
    if (!queryParams) return null
    const hasSearchParams = Object.entries(queryParams)
      .filter(([key]) => key !== "page")
      .some(
        ([_, value]) =>
          value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true)
      )

    if (!hasSearchParams) return null

    return {
      propertyId: queryParams.propertyId || undefined,
      propertyName: queryParams.propertyName || "",
      propertyNameKana: queryParams.propertyNameKana || "",
      postalCode: queryParams.postalCode || "",
      address: queryParams.address || "",
      ownerId: queryParams.ownerId || undefined,
      ownerName: queryParams.ownerName || "",
      managementCompany: queryParams.managementCompany || "",
      isEchoesLinked: normalizeEchoesLinkedFilter(queryParams.isEchoesLinked),
    } as SearchFormValues
  }, [queryParams])

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      propertyId: queryParams.propertyId || undefined,
      propertyName: queryParams.propertyName || "",
      propertyNameKana: queryParams.propertyNameKana || "",
      postalCode: queryParams.postalCode || "",
      address: queryParams.address || "",
      ownerName: queryParams.ownerName || "",
      managementCompany: queryParams.managementCompany || "",
      isEchoesLinked: normalizeEchoesLinkedFilter(queryParams.isEchoesLinked),
    },
  })

  const onSubmit = (formData: SearchFormValues) => {
    // URLパラメーターを更新
    setQueryParams({
      page: 1, // 検索時はページを1にリセット
      propertyId: formData.propertyId || null,
      propertyName: formData.propertyName || null,
      propertyNameKana: formData.propertyNameKana || null,
      postalCode: formData.postalCode || null,
      address: formData.address || null,
      ownerId: null,
      ownerName: formData.ownerName || null,
      managementCompany: formData.managementCompany || null,
      isEchoesLinked: formData.isEchoesLinked === "all" ? null : formData.isEchoesLinked || null,
    })
    queryClient.invalidateQueries({ queryKey: ["/properties"] })
  }

  const onReset = () => {
    form.reset()
    // URLパラメーターをクリア
    setQueryParams({
      page: null,
      propertyId: null,
      propertyName: null,
      propertyNameKana: null,
      postalCode: null,
      address: null,
      ownerId: null,
      ownerName: null,
      managementCompany: null,
      isEchoesLinked: null,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">物件一覧</h1>

      <div className="">
        <div className="p-4 border-b bg-white rounded-lg shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="propertyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>物件ID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder=""
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? Number.parseInt(e.target.value, 10) : undefined
                            )
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>物件名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="propertyNameKana"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>物件名（カナ）</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>郵便番号</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>住所</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="ownerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>オーナー名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="managementCompany"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>建物管理会社</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isEchoesLinked"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Echoes連携</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          options={echoesLinkedFilterOptions}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" variant="default">
                  検索
                </Button>
                <Button type="button" variant="outline" onClick={onReset}>
                  検索をリセット
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <ReactErrorBoundary>
          <Suspense fallback={<PropertyPageFallback />}>
            <PropertyPageContent
              searchParams={searchParams}
              currentPage={queryParams.page}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
            />
          </Suspense>
        </ReactErrorBoundary>
      </div>
    </div>
  )
}

interface PropertyPageContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
}

type PropertySortKey = "id" | "name"
type PropertySortOrder = "asc" | "desc"
type PropertySort = `${PropertySortKey}-${PropertySortOrder}`

function PropertyPageContent({
  searchParams,
  currentPage,
  onPageChange,
  itemsPerPage,
}: PropertyPageContentProps) {
  const navigate = useNavigate()
  const [propertySort, setPropertySort] = useState<PropertySort>("id-asc")
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<PropertyList200PropertiesItem | null>(
    null
  )

  const handleDeleteClick = (property: PropertyList200PropertiesItem) => {
    setSelectedProperty(property)
    setIsDeleteModalOpen(true)
  }

  const handleEdit = (id: number) => {
    navigate(`/properties/${id}/edit`)
  }

  const handleDetail = (id: number) => {
    openInNewTab(`/properties/${id}`)
  }

  const apiParams = useMemo(() => {
    const [sortBy, sortOrder] = propertySort.split("-") as [PropertySortKey, PropertySortOrder]
    const params: PropertyModels.PropertyListParams = {
      page: currentPage,
      limit: itemsPerPage,
      withRooms: PropertyListWithRooms.true,
      withOwner: PropertyListWithOwner.true,
      sortBy,
      sortOrder,
    }

    if (searchParams?.propertyId) {
      params.propertyId = searchParams.propertyId
    }
    if (searchParams?.propertyName) {
      params.propertyName = searchParams.propertyName
    }
    if (searchParams?.propertyNameKana) {
      params.propertyNameKana = searchParams.propertyNameKana
    }
    if (searchParams?.postalCode) {
      params.postalCode = searchParams.postalCode
    }
    if (searchParams?.address) {
      params.address = searchParams.address
    }
    if (searchParams?.ownerId) {
      params.ownerId = searchParams.ownerId
    }
    if (searchParams?.ownerName) {
      params.ownerName = searchParams.ownerName
    }
    if (searchParams?.managementCompany) {
      params.managementCompany = searchParams.managementCompany
    }
    if (searchParams?.isEchoesLinked === "true") {
      params.isEchoesLinked = PropertyListIsEchoesLinked.true
    } else if (searchParams?.isEchoesLinked === "false") {
      params.isEchoesLinked = PropertyListIsEchoesLinked.false
    }

    return params
  }, [searchParams, currentPage, itemsPerPage, propertySort])

  // APIリクエスト
  const { data } = propertyApi.usePropertyListSuspense(apiParams)

  // ページネーション計算
  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / itemsPerPage)
  const properties = data.properties
  const propertySortLabel =
    propertySort === "id-asc"
      ? "物件ID 昇順"
      : propertySort === "id-desc"
        ? "物件ID 降順"
        : propertySort === "name-asc"
          ? "物件名 昇順"
          : "物件名 降順"

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const halfRange = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={(e) => {
              e.preventDefault()
              handlePageChange(i)
            }}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }
    return pages
  }

  const currentYear = new Date().getFullYear()

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{totalCount}件の物件が見つかりました</div>
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline">
                <Sliders className="mr-2" size={16} />
                表示順: {propertySortLabel}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem
                onClick={() => {
                  setPropertySort("id-asc")
                  onPageChange(1)
                }}
              >
                物件ID 昇順
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setPropertySort("id-desc")
                  onPageChange(1)
                }}
              >
                物件ID 降順
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setPropertySort("name-asc")
                  onPageChange(1)
                }}
              >
                物件名 昇順
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setPropertySort("name-desc")
                  onPageChange(1)
                }}
              >
                物件名 降順
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <PermissionGate screenId="properties" action="create">
            <Link to="/properties/add">
              <Button>
                <Plus className="mr-2" size={16} />
                新規物件登録
              </Button>
            </Link>
          </PermissionGate>
        </div>
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">物件ID</TableHead>
              <TableHead className="whitespace-nowrap">物件名</TableHead>
              <TableHead className="whitespace-nowrap">物件名（カナ）</TableHead>
              <TableHead className="whitespace-nowrap">郵便番号</TableHead>
              <TableHead className="whitespace-nowrap">住所</TableHead>
              <TableHead className="whitespace-nowrap">オーナー名</TableHead>
              <TableHead className="whitespace-nowrap">築年数</TableHead>
              <TableHead className="whitespace-nowrap">登録戸数</TableHead>
              <TableHead className="whitespace-nowrap">建物管理会社</TableHead>
              <TableHead className="whitespace-nowrap">Echoes連携</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {properties.map((property) => (
              <TableRow key={property.id}>
                <TableCell>{property.id}</TableCell>
                <TableCell className="text-blue-600 cursor-pointer hover:underline">
                  <Link to={`/properties/${property.id}`} target="_blank" rel="noopener noreferrer">
                    {property.name}
                  </Link>
                </TableCell>
                <TableCell>{property.nameKana}</TableCell>
                <TableCell>
                  〒{property.upperZip}-{property.lowerZip}
                </TableCell>
                <TableCell className="truncate">
                  {property.prefecture}
                  {property.city}
                  {property.town}
                  {property.district}
                </TableCell>
                <TableCell>{property.owner?.name || "-"}</TableCell>
                <TableCell>
                  築{currentYear - property.builtYear}年（{property.builtYear}年）
                </TableCell>
                <TableCell>{property.roomCount ?? 0}戸</TableCell>
                <TableCell className="truncate">{property.managementCompanyName || "-"}</TableCell>
                <TableCell>
                  <EchoesSyncCell
                    echoesId={property.echoesBuildingId}
                    echoesFailedAt={property.echoesFailedAt}
                  />
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[200px]">
                      <DropdownMenuItem onClick={() => handleDetail(property.id)}>
                        <Book className="text-slate-600 mr-2" size={16} />
                        詳細
                      </DropdownMenuItem>
                      <PermissionGate screenId="properties" action="update">
                        <DropdownMenuItem onClick={() => handleEdit(property.id)}>
                          <Edit className="text-slate-600 mr-2" size={16} />
                          編集
                        </DropdownMenuItem>
                      </PermissionGate>
                      <PermissionGate screenId="properties" action="delete">
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(property)}
                          className="text-red-600"
                        >
                          <Trash className="text-slate-600 mr-2" size={16} />
                          削除
                        </DropdownMenuItem>
                      </PermissionGate>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {properties.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8">
                  物件が見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {totalCount} 件中 {(currentPage - 1) * itemsPerPage + 1} -{" "}
            {Math.min(currentPage * itemsPerPage, totalCount)} 件を表示
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationFirst
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(1)
                  }}
                  disabled={currentPage === 1}
                  className="cursor-pointer"
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(currentPage - 1)
                  }}
                  disabled={currentPage === 1}
                  className="cursor-pointer"
                />
              </PaginationItem>
              {renderPageNumbers()}
              <PaginationItem>
                <PaginationNext
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(currentPage + 1)
                  }}
                  disabled={currentPage === totalPages}
                  className="cursor-pointer"
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLast
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(totalPages)
                  }}
                  disabled={currentPage === totalPages}
                  className="cursor-pointer"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <DeletePropertyModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        property={selectedProperty}
      />
    </div>
  )
}

interface DeletePropertyModalProps {
  isOpen: boolean
  onClose: () => void
  property: PropertyList200PropertiesItem | null
}

function DeletePropertyModal({ isOpen, onClose, property }: DeletePropertyModalProps) {
  const handleDelete = () => {
    if (!property) return
    // TODO: API呼び出し
    console.log("削除対象:", property.id)
    toast({
      title: "削除しました（モック）",
    })
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            本当に削除してよろしいですか？
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>削除した物件は復元できません。</DialogDescription>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete}>
            削除する
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PropertyPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
